<template>
  <div class="graph-module">
    <div class="graph-header">
      <div
        v-if="dashboardModule.name"
        class="module-name">
        {{ dashboardModule.name }}
      </div>

      <div
        v-if="drilldownPath?.length"
        class="drilldown-path">
        <div class="drilldown-items">
          <div
            v-for="(item, index) in drilldownPath"
            :key="index"
            class="drilldown-item">
            <div class="drilldown-content">
              <div class="drilldown-value">
                {{ item.value.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="actions">
        <button
          v-if="$screen.is('sm')"
          class="button is-small mr-2"
          @click="isSettingsSidebarOpen = true">
          Options
        </button>
        <SnapQLDebugButton
          :dashboard-module="dashboardModule"
          :dashboard-data-context="dashboardDataContext"
          :dashboard-key="dashboardKey" />
        <ExportButton
          :dashboard-module="dashboardModule"
          :dashboard-data-context="moduleDataContext"
          :dashboard-key="dashboardKey" />
        <ShareButton
          :dashboard-module="dashboardModule"
          :dashboard-key="dashboardKey"
          :shared-module-id="sharedModuleId"
          @shared="$emit('module-shared', { moduleKey: dashboardModule.key, sharedModuleId: $event })"
          @unshared="$emit('module-unshared', { moduleKey: dashboardModule.key })" />
      </div>
    </div>
    <div class="graph-body">
      <div class="graph-container">
        <template v-if="isLoading">
          <b-skeleton
            height="568px"
            width="100%"
            :animated="true" />
        </template>
        <div
          v-else-if="queryError"
          class="query-error">
          <b-icon
            icon="alert-circle-outline"
            size="is-small" />
          <span>{{ queryError }}</span>
        </div>
        <template v-else>
          <BarChart
            v-if="type === 'bar'"
            :y-axis-data-type="yAxisDataType"
            :y-axis-data-types="yAxisDataTypes"
            :y-axis-data-formats="yAxisDataFormats"
            :data="filteredChartItems"
            :measure-label="measureLabel"
            :measure-labels="measureLabels"
            :benchmark-label="benchmarkLabel"
            :benchmark-labels="benchmarkLabels"
            :avg-benchmark="avgBenchmark"
            @onDrilldown="handleDrilldown" />
          <LineChart
            v-if="type === 'line'"
            :x-axis-labels="xAxisLabels"
            :y-axis-data-type="yAxisDataType"
            :y-axis-data-types="yAxisDataTypes"
            :y-axis-data-formats="yAxisDataFormats"
            :data="filteredChartItems"
            @onDrilldown="handleDrilldown" />
        </template>
      </div>
      <GraphControls
        v-if="!$screen.is('sm')"
        v-model="chartItems"
        :sort-options="sortOptions"
        :sort-option.sync="sortOption"
        :is-loading="isLoading"
        :has-average="!!avgBenchmark"
        style="width: 360px"
        :type="type" />
    </div>
    <drawer
      v-if="$screen.is('sm')"
      :is-visible.sync="isSettingsSidebarOpen">
      <GraphControls
        v-model="chartItems"
        :sort-options="sortOptions"
        :sort-option.sync="sortOption"
        :is-loading="isLoading"
        :has-average="!!avgBenchmark"
        :type="type" />
    </drawer>
  </div>
</template>

<script>
  import BarChart from './BarChart.vue'
  import LineChart from './LineChart.vue'
  import GraphControls from './GraphControls.vue'
  import ExportButton from '../ExportButton.vue'
  import SnapQLDebugButton from '../SnapQLDebugButton.vue'
  import ShareButton from '../ShareButton.vue'
  import axios from 'axios'
  import Drawer from '@/components/ui/Drawer.vue'

  const colors = {
    twilightBlue: '#438A9D',
    duskPurple: '#7F70CF',
    auroraGreen: '#67CD9C',
    duskRed: '#B85C81',
    duskOrange: '#F17C57',
    sunriseYellow: '#F6C856',
    middayblue: '#52AFC8',
    twilightPurple: '#8A88E9',
    dawnOrange: '#F69D4C',
    morningBlue: '#91D1E3',
    sunsetPine: '#D592E8',
  }
  const measureLineStyles = ['solid', 'dashed', 'dotted',]

  export default {
    name: 'GraphModule',
    components: { BarChart, LineChart, GraphControls, ExportButton, SnapQLDebugButton, ShareButton, Drawer, },
    props: {
      isReady: {
        type: Boolean,
        required: true,
      },
      type: {
        type: String,
        required: true,
      },
      dashboardModule: {
        type: Object,
        required: true,
      },
      dashboardDataContext: {
        type: Object,
        required: true,
      },
      dashboardKey: {
        type: String,
        required: true,
      },
      sharedModuleId: {
        type: String,
        default: undefined,
      },
    },
    data () {
      return {
        searchQuery: '',
        chartItems: [],
        isLoading: true,
        queryError: null,
        isExporting: false,
        isSettingsSidebarOpen: false,
        cancelTokenSource: null,
        avgBenchmark: null,

        xAxisLabels: [],
        yAxisDataType: null,
        yAxisDataTypes: [],
        yAxisDataFormats: [],
        benchmarkLabel: '',
        measureLabel: '',
        measureLabels: [],
        benchmarkLabels: [],

        behaviors: {
          drilldown: {
            control: null,
          },
        },
        sortOption: null,
      }
    },
    computed: {
      sortOptions () {
        if (this.type == 'bar') {
          return [
            {
              key: 'value_desc',
              label: 'Descending Measure',
              moduleSort: {
                sort_by: 'measure',
                ascending: false,
              },
            },
            {
              key: 'value_asc',
              label: 'Ascending Measure',
              moduleSort: {
                sort_by: 'measure',
                ascending: true,
              },
            },
            {
              key: 'label_desc',
              label: 'Descending Name',
              moduleSort: {
                sort_by: 'dimension',
                ascending: false,
              },
            },
            {
              key: 'label_asc',
              label: 'Ascending Name',
              moduleSort: {
                sort_by: 'dimension',
                ascending: true,
              },
            },
          ]
        } else { // TODO: Add sort for line charts (?)
          return []
        }
      },

      filteredChartItems () {
        if (this.type === 'line') {
          return this.chartItems
            .filter((group) => group.isActive)
            .flatMap((group) =>
              group.lines.map((line, lineIndex) => ({
                ...line,
                id: `${group.id}-${line.id}`,
                label: group.label,
                groupLabel: group.label,
                measureLabel: line.label,
                measureIndex: lineIndex,
                color: group.color,
                opacity: lineIndex === 0 ? 1 : 0.5,
                lineStyleType:
                  measureLineStyles[lineIndex % measureLineStyles.length],
              }))
            )
        }

        return this.chartItems.filter((item) => item.isActive)

      },

      drilldownPath () {
        return (
          this.dashboardDataContext?.control_selections?.[
            this.behaviors?.drilldown?.control
          ]?.drilldown_path || []
        )
      },

      moduleDataContext () {
        if (this.sortOption != null) {
          return {
            ...this.dashboardDataContext,
            module_sort: this.sortOption.moduleSort,
          }
        } else {
          return this.dashboardDataContext
        }
      },
    },

    watch: {
      dashboardDataContext: {
        handler () {
          this.loadData()
        },
        deep: true,
      },
    },

    mounted () {
      this.loadData()
    },

    beforeDestroy () {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('Component destroyed')
      }
      if (this.abortController) {
        this.abortController.abort()
      }
    },

    methods: {
      handleDrilldown (data) {
        this.$emit('onDrilldown', {
          ...data,
          control: this.behaviors?.drilldown?.control,
        })
      },
      async loadData () {
        if (!this.isReady) {
          return
        }
        const useSnapQL = this.$hasFeatureFlag('USE_SNAPQL')
        if (useSnapQL) {
          return this.loadDataStreaming()
        }
        try {
          // Cancel previous request if it exists
          if (this.cancelTokenSource) {
            this.cancelTokenSource.cancel('New request initiated')
          }

          // Create new CancelToken
          this.cancelTokenSource = axios.CancelToken.source()

          this.isLoading = true
          this.queryError = null

          const response = await this.$apis.companyConfigs.getDashboardModuleData(
            this.dashboardKey,
            this.dashboardModule.key,
            this.dashboardDataContext,
            { cancelToken: this.cancelTokenSource.token, }
          )
          if (response?.error) {
            if (response?.code !== 'ERR_CANCELED') {
              this.$buefy.toast.open({
                message: 'Failed to load Chart Data',
                type: 'is-warning',
              })
            }
            return
          }

          this.applyChartResponse(response)
          this.isLoading = false
        } catch (error) {
          console.log(error)
        }
      },
      applyChartResponse (response) {
        if (this.type === 'bar') {
          this.chartItems = response.bar_groups.map((item, index) => ({
            ...item,
            color: Object.values(colors)[index % Object.keys(colors).length],
            isActive: item.isActive !== undefined ? item.isActive : true,
            conditional_quality: item.bars?.[0]?.conditional_quality || null,
          }))
          this.measureLabels = response.measure_labels
          this.benchmarkLabels = response.benchmark_labels

          this.yAxisDataTypes = response.y_axis_data_types || []
          this.yAxisDataType = this.yAxisDataTypes[0] || null
          this.yAxisDataFormats = response.y_axis_data_formats || []
          this.avgBenchmark = response.avg_benchmark
          if (response.avg_benchmark && !this.sortOption) {
            this.sortOption = this.sortOptions.find((option) => option.key === 'value_desc')
          }
        } else if (this.type === 'line') {
          this.chartItems = response.line_groups.map((group, index) => ({
            ...group,
            id: group.id,
            color: Object.values(colors)[index % Object.keys(colors).length],
            isActive: true,
          }))
          this.yAxisDataTypes = response.y_axis_data_types || []
          this.yAxisDataType = this.yAxisDataTypes[0] || null
          this.yAxisDataFormats = response.y_axis_data_formats || []
          this.xAxisLabels = response.x_axis_labels
        }
        this.measureLabel = this.measureLabels[0] || ''
        this.benchmarkLabel = this.benchmarkLabels[0] || ''
        this.behaviors = response.behaviors
      },
      appendBars (bars) {
        const startIndex = this.chartItems.length
        const newBars = bars.map((item, index) => ({
          ...item,
          color: Object.values(colors)[(startIndex + index) % Object.keys(colors).length],
          isActive: item.isActive !== undefined ? item.isActive : true,
        }))
        this.chartItems = this.chartItems.concat(newBars)
      },
      async loadDataStreaming () {
        if (this.abortController) {
          this.abortController.abort()
        }
        this.abortController = new AbortController()
        this.isLoading = true
        this.queryError = null

        await this.$apis.companyConfigs.streamDashboardModuleData(
          this.dashboardKey,
          this.dashboardModule.key,
          this.dashboardDataContext,
          {
            // Line charts: single complete payload
            data: (payload) => {
              this.applyChartResponse(payload)
            },
            // Bar charts: streamed metadata → bars → complete
            metadata: (payload) => {
              this.applyChartResponse(payload)
            },
            bars: (bars) => {
              this.appendBars(bars)
            },
            complete: (payload) => {
              if (payload?.avg_benchmark) {
                this.avgBenchmark = payload.avg_benchmark
                if (!this.sortOption) {
                  this.sortOption = this.sortOptions.find(
                    (option) => option.key === 'value_desc'
                  )
                }
              }
              this.isLoading = false
            },
            truncated: (payload) => {
              this.$buefy.toast.open({
                message: payload.message,
                type: 'is-warning',
                duration: 8000,
              })
              this.isLoading = false
            },
            error: (err) => {
              this.queryError = err.message || 'Failed to load chart data'
              this.isLoading = false
            },
          },
          this.abortController.signal
        )
      },
    },
  }
</script>

<style scoped lang="scss">
.query-error {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 16px;
  color: #b5451b;
  font-size: 13px;
}
.graph-module {
  width: 100%;
  height: 100%;
  display: flex;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  flex-direction: column;
  background-color: white;

  @include mobile {
    border-radius: 0px;
  }
}
.graph-header {
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 16px;
  color: #333;
  padding: 8px;
  display: flex;
  align-items: center;

  .module-name {
    margin-right: 12px;
  }

  .drilldown-path {
    .drilldown-items {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      align-items: center;
    }

    .drilldown-item {
      display: flex;
      align-items: center;
      background: #f5f5f5;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;

      .drilldown-content {
        display: flex;
        gap: 4px;
      }

      .drilldown-option {
        color: #666;
        font-weight: 500;
      }

      .drilldown-value {
        color: #333;
      }

      .mdi-chevron-right {
        color: #999;
        margin: 0 4px;
      }
    }
  }

  .actions {
    margin-left: auto;

    .mdi-download {
      font-size: 18px;
    }
  }
}
.graph-body {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.graph-container {
  height: 600px;
  width: calc(100% - 360px);
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @include mobile {
    width: 100%;
  }
}
</style>
